import fpFetch from "@/services/fpFetch";
import React, { useEffect, useState, useRef } from "react";
import { convertMarkdownStringToHtmlString } from "../FpAppV4/services/stringMarkdownOpertations";
import "./../FpAppV4/tailwind_defaults_override_style.css";
import { useSelector } from "react-redux";
import {
  highlightTextNodes,
  removeNodeHighlights,
} from "../common/utils/addOnServices/highlightTextNodes";
import { tailwindOverride } from "../common/DocxViewer/tailwindOverride";
import { processEmailHtml, sanitizeEmailHtml } from "./safeEmailProcessor";

const EmailViewer = ({ fileUrl }: { fileUrl: string }) => {
  const [emailData, setEmailData] = useState<EmailData | null>(null);
  const [emailHeaders, setEmailHeaders] = useState<Record<string, string>>({});
  const [emailBody, setEmailBody] = useState("");
  const [processedEmail, setProcessedEmail] = useState<{
    html: string;
    css: string;
  }>({ html: "", css: "" });
  const fileViewerData = useSelector((state: any) => state.fileViewerData);
  const originalHtmlRef = useRef<string>("");
  const emailContainerRef = useRef<HTMLDivElement | null>(null);

  const decodeBase64 = (str: string) => {
    str = str.replace(/\s/g, "");
    str = str.replace(/-/g, "+").replace(/_/g, "/");
    while (str.length % 4) {
      str += "=";
    }

    // Decode Base64 and properly handle UTF-8 encoding
    try {
      // atob returns a binary string where each character represents a byte
      const binaryStr = atob(str);

      // Convert binary string to UTF-8
      // Use TextDecoder for proper UTF-8 decoding
      const bytes = new Uint8Array(binaryStr.length);
      for (let i = 0; i < binaryStr.length; i++) {
        bytes[i] = binaryStr.charCodeAt(i);
      }

      const decoder = new TextDecoder("utf-8");
      return decoder.decode(bytes);
    } catch (e) {
      console.error("Error decoding base64:", e);
      // Fallback to original behavior
      return atob(str);
    }
  };

  type ExtractedContent = {
    text: string;
    html: string;
  };

  function extractBodyParts(part: any): ExtractedContent {
    if (!part) return { text: "", html: "" };
    let text = "";
    let html = "";
    if (!part.mimeType) return { text, html };
    if (part.mimeType === "text/plain") {
      text = decodeBase64(part.body?.data || "");
    } else if (part.mimeType === "text/html") {
      html = decodeBase64(part.body?.data || "");
    } else if (part.mimeType.startsWith("multipart/") && part.parts) {
      for (const subPart of part.parts) {
        const result = extractBodyParts(subPart);
        text += result.text;
        html += result.html;
      }
    }
    return { text, html };
  }

  const processEmailData = async (data: EmailData) => {
    if (data?.payload?.headers) {
      let headers: Record<string, string> = {};
      for (const header of data.payload.headers) {
        headers[header.name] = header.value;
      }
      setEmailHeaders(headers);
    }
    if (!data?.payload?.mimeType) return;
    const { text: rawText, html: rawHtml } = extractBodyParts(data.payload);
    const convertedTextToHtml = rawText
      ? await convertMarkdownStringToHtmlString(rawText)
      : "";
    const finalContent = rawHtml || convertedTextToHtml || "";

    // Process the email content safely
    const sanitizedHtml = sanitizeEmailHtml(finalContent);
    const processed = processEmailHtml(sanitizedHtml, "email-container");

    setEmailBody(finalContent); // Keep original for compatibility
    setProcessedEmail(processed);
  };

  useEffect(() => {
    if (!emailData) return;
    processEmailData(emailData);
  }, [emailData]);

  useEffect(() => {
    fpFetch(fileUrl)
      .then((res) => res.json())
      .then((data: EmailData) => {
        setEmailData(data);
      })
      .catch(console.error);
  }, [fileUrl]);

  // Store original HTML when it changes, and capture it from DOM after initial render
  useEffect(() => {
    if (processedEmail.html) {
      // If we don't have the original HTML stored yet, capture it from the DOM
      // This handles the initial render case where dangerouslySetInnerHTML sets it
      if (!originalHtmlRef.current && emailContainerRef.current) {
        // Wait for DOM to be updated
        requestAnimationFrame(() => {
          if (emailContainerRef.current) {
            originalHtmlRef.current = emailContainerRef.current.innerHTML;
          }
        });
      } else if (processedEmail.html !== originalHtmlRef.current) {
        // HTML changed, update the stored version
        originalHtmlRef.current = processedEmail.html;
      }
    }
  }, [processedEmail.html]);

  // Apply highlighting when fileViewerData changes
  useEffect(() => {
    if (!processedEmail.html || !emailContainerRef.current) return;

    // Restore original HTML first to ensure clean state before highlighting
    if (originalHtmlRef.current) {
      emailContainerRef.current.innerHTML = originalHtmlRef.current;
    }

    // Use requestAnimationFrame to ensure DOM is updated before highlighting
    requestAnimationFrame(() => {
      if (emailContainerRef.current) {
        highlightTextNodes(".email-content", fileViewerData);
      }
    });
  }, [processedEmail.html, fileViewerData]);

  return (
    <div
      className="w-full h-full bg-white overflow-auto p-8"
      onMouseDown={(e) => {
        // Handle link clicks on mousedown to avoid interfering with text selection
        const target = e.target as HTMLElement;
        const link = target.closest("a");

        if (link && link.href) {
          e.preventDefault();
          e.stopPropagation();
          window.open(link.href, "_blank", "noopener,noreferrer");
        }
      }}
      onClick={(e) => {
        // Check if user has an active text selection - if so, don't reset highlighting
        const selection = window.getSelection();
        const hasSelection = selection && selection.toString().length > 0;

        // Also check if the selection is within the email content area
        if (hasSelection && emailContainerRef.current) {
          const range =
            selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
          if (
            range &&
            emailContainerRef.current.contains(range.commonAncestorContainer)
          ) {
            // User is selecting text within email content, don't interfere
            return;
          }
        }

        // Only reset highlighting if there's no active selection
        if (!hasSelection) {
          // Restore original HTML to fully reset highlighting
          if (emailContainerRef.current && originalHtmlRef.current) {
            emailContainerRef.current.innerHTML = originalHtmlRef.current;
          } else {
            removeNodeHighlights(".email-content");
          }
        }
      }}
    >
      <style>{tailwindOverride}</style>
      {/* Scoped CSS for email content */}
      {processedEmail.css && (
        <style dangerouslySetInnerHTML={{ __html: processedEmail.css }} />
      )}
      <div className="flex flex-col gap-2 mb-4">
        {emailHeaders["Subject"] && (
          <div className="text-base font-medium text-gray-700">
            Subject: {emailHeaders["Subject"]}
          </div>
        )}
        {emailHeaders["From"] && (
          <div className="text-sm text-gray-500">
            From: {emailHeaders["From"]}
          </div>
        )}
        {emailHeaders["To"] && (
          <div className="text-sm text-gray-500">To: {emailHeaders["To"]}</div>
        )}
        {emailHeaders["Date"] &&
          !isNaN(new Date(emailHeaders["Date"]).getTime()) && (
            <div className="text-sm text-gray-500">
              Date:{" "}
              {new Date(emailHeaders["Date"]).toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "numeric",
                minute: "numeric",
              })}
            </div>
          )}
      </div>
      <div
        ref={emailContainerRef}
        id="email-container"
        className="email-content tailwind-override"
        dangerouslySetInnerHTML={{ __html: processedEmail.html }}
      />
    </div>
  );
};

export default EmailViewer;
